import React from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

function cn(...args: Array<string | false | null | undefined>) {
    return args.filter(Boolean).join(' ');
}

const skeletonStyles = tv({
    base: 'relative overflow-hidden rounded',
    variants: {
        variant: {
            text: 'h-4 w-full',
            title: 'h-6 w-3/4',
            subtitle: 'h-4 w-1/2',
            circle: 'rounded-full',
            rectangle: 'rounded-lg',
            card: 'h-32 w-full rounded-xl',
        },
    },
    defaultVariants: {
        variant: 'text',
    },
});

export interface SkeletonProps
    extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonStyles> {
    width?: string | number;
    height?: string | number;
    count?: number;
    delay?: string;
}

const Skeleton: React.FC<SkeletonProps> = ({
    variant = 'text',
    width,
    height,
    count = 1,
    delay = '0s',
    className,
    style,
    ...rest
}) => {
    const skeletonClass = skeletonStyles({ variant });

    const skeletonStyle: React.CSSProperties = {
        ...style,
        backgroundColor: 'var(--v2-skeleton-base)',
        ...(width && { width: typeof width === 'number' ? `${width}px` : width }),
        ...(height && { height: typeof height === 'number' ? `${height}px` : height }),
    };

    const renderSkeleton = (key?: number) => (
        <div
            key={key}
            className={cn(skeletonClass, className)}
            style={skeletonStyle}
            {...rest}
        >
            <div
                className="absolute inset-0 -translate-x-full animate-shimmer"
                style={{
                    backgroundImage: `linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)`,
                    animationDelay: delay,
                }}
            />
        </div>
    );

    if (count > 1) {
        return (
            <>
                {Array.from({ length: count }).map((_, index) =>
                    renderSkeleton(index)
                )}
            </>
        );
    }

    return renderSkeleton();
};

export default Skeleton;
